/**
 * Resolve the `src` for a merch image.
 *
 * Our own S3 objects are private, so they have to go through
 * `/api/assets/serve`, which signs them. Print-provider renders do not:
 * they are public objects in Shirtigo's own AWS account, and the proxy
 * rejects them outright — it only accepts endpoints we hold credentials
 * for, and answers "URL does not match a known S3 endpoint" otherwise.
 * Routing them through it is exactly why shop thumbnails stayed blank
 * while the same URLs loaded fine in the creator panel, which links them
 * directly.
 *
 * Keeping the allowlist here rather than widening the proxy preserves the
 * proxy's SSRF guard: we are not making it fetch arbitrary hosts, we are
 * choosing not to involve it for objects that need no signing.
 */

/** Buckets whose objects are public renders from the print provider. */
const PUBLIC_PRINT_BUCKETS = [
    'seedshirt-production-uploads', // rendered products (shirt + artwork)
    'shirtigo-assets',              // base product photos, mockups, designs
]

export function merchImageSrc(url?: string | null): string {
    if (!url) return ''
    const raw = String(url)

    // Data-URIs and app-relative paths are already directly usable.
    if (raw.startsWith('data:') || !raw.startsWith('http')) return raw

    try {
        const parsed = new URL(raw)
        // Path-style S3 URLs put the bucket in the first path segment:
        // https://s3-eu-west-1.amazonaws.com/{bucket}/{key}
        const firstSegment = parsed.pathname.split('/').filter(Boolean)[0]
        if (firstSegment && PUBLIC_PRINT_BUCKETS.includes(firstSegment)) return raw
        // Virtual-host style: https://{bucket}.s3.{region}.amazonaws.com/{key}
        if (PUBLIC_PRINT_BUCKETS.some(b => parsed.hostname.startsWith(`${b}.`))) return raw
    } catch {
        // Unparseable input falls through to the proxy, which validates it.
    }

    return `/api/assets/serve?url=${encodeURIComponent(raw)}`
}
